import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import { LockOutlined as LockOutlinedIcon } from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import { useApi } from 'lib/api';
import { puedeUsarIniciarNomina } from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, fpInputSx } from 'components/financepro';

export { puedeUsarIniciarNomina };

const btnBase = { textTransform: 'none', borderRadius: 1.8, fontWeight: 700, boxShadow: 'none' };
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: '#FFFFFF',
  border: `1px solid ${c}`,
  '&:hover': { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
  '&.Mui-disabled': { color: FP.color.textMuted, borderColor: FP.color.border },
});
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: '#FFFFFF',
  '&:hover': { bgcolor: hover, boxShadow: 'none', color: '#FFFFFF' },
  '&.Mui-disabled': { bgcolor: `${bg}66`, color: '#FFFFFF' },
});

function formatoFecha(valor) {
  const texto = String(valor || '').trim();
  if (!texto || texto.length < 10) return '—';
  const [y, m, d] = texto.slice(0, 10).split('-');
  return d && m && y ? `${d}-${m}-${y}` : texto;
}

const cardShellSx = {
  borderRadius: 2.5,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  border: `1px solid ${FP.color.border}`,
  overflow: 'hidden',
};

/**
 * Iniciar nómina (fps8000a). Crea el archivo `pagosnom` para novedades.
 * El menú y Acciones solo la muestran si el rol tiene `iniciarnomina`.
 */
const IniciarNomina = ({ open = false, onClose, embedded = false }) => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { usuarioLog, roleDetalles } = controller;
  const { post } = useApi();

  const puedeUsar = useMemo(() => puedeUsarIniciarNomina(roleDetalles), [roleDetalles]);
  const puedeOperar = Boolean(selectedCompany?.db_empresa && usuarioLog);

  const [cargando, setCargando] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [periodo, setPeriodo] = useState(null);
  const [periodicidad, setPeriodicidad] = useState('');
  const [horas, setHoras] = useState(0);
  const [empleados, setEmpleados] = useState(0);
  const [yaIniciada, setYaIniciada] = useState(false);

  const credencialesEmpresa = useCallback(
    () => ({ id_empresa: selectedCompany }),
    [selectedCompany]
  );

  const cargar = useCallback(async () => {
    if (!puedeUsar || !puedeOperar) {
      setPeriodo(null);
      return;
    }
    setCargando(true);
    try {
      const { ok, data } = await post('/api/nomina_obtenerperiodo', credencialesEmpresa());
      if (!ok) throw new Error(data.Error || 'Error al leer el período de nómina');
      setPeriodo(data.periodo || null);
      setPeriodicidad(data.periodicidad || '');
      setHoras(Number(data.horas) || 0);
      setEmpleados(Number(data.empleados) || 0);
      setYaIniciada(Boolean(data.ya_iniciada));
    } catch (e) {
      setPeriodo(null);
      showError(e.message || 'Error al leer el período de nómina');
    } finally {
      setCargando(false);
    }
  }, [puedeUsar, puedeOperar, post, credencialesEmpresa, showError]);

  useEffect(() => {
    if ((open || embedded) && puedeUsar && puedeOperar) cargar();
  }, [open, embedded, puedeUsar, puedeOperar, cargar]);

  const cerrar = () => {
    if (procesando) return;
    if (onClose) onClose();
  };

  const procesar = async () => {
    if (!puedeUsar) {
      showError('No tiene permisos para iniciar la nómina');
      return;
    }
    setProcesando(true);
    try {
      const { ok, data } = await post('/api/nomina_iniciar', credencialesEmpresa());
      if (!ok) throw new Error(data.Error || 'No se pueden grabar los datos');
      showSuccess(data.ok || 'Archivo de nómina creado para novedades');
      if (onClose) onClose();
      else await cargar();
    } catch (e) {
      showError(e.message || 'No se pueden grabar los datos');
    } finally {
      setProcesando(false);
    }
  };

  const cuerpo = !puedeOperar ? (
    <Typography sx={{ color: FP.color.textSecondary, py: 2 }}>
      Seleccione una empresa para iniciar la nómina.
    </Typography>
  ) : cargando ? (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
      <CircularProgress size={28} sx={{ color: FP.color.navy }} />
    </Box>
  ) : (
    <Grid container spacing={2} sx={{ mt: 0 }}>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Año"
          value={periodo?.ano ?? ''}
          InputProps={{ readOnly: true }}
          InputLabelProps={{ shrink: true }}
          sx={fpInputSx}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Nómina"
          value={periodo?.numero ?? ''}
          InputProps={{ readOnly: true }}
          InputLabelProps={{ shrink: true }}
          sx={fpInputSx}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Periodicidad"
          value={periodicidad}
          helperText="Tomada de Nómina inicial"
          InputProps={{ readOnly: true }}
          InputLabelProps={{ shrink: true }}
          sx={fpInputSx}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Horas del período"
          value={horas || ''}
          helperText="Sin horas extras"
          InputProps={{ readOnly: true }}
          InputLabelProps={{ shrink: true }}
          sx={fpInputSx}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.85rem' }}>
          Período {formatoFecha(periodo?.fecha1)} al {formatoFecha(periodo?.fecha2)} · {empleados}{' '}
          empleado{empleados === 1 ? '' : 's'}
          {yaIniciada ? '. Ya existe un archivo; se regenerará y se conservarán las novedades.' : '.'}
        </Typography>
      </Grid>
    </Grid>
  );

  const acciones = (
    <>
      {onClose ? (
        <Button onClick={cerrar} disabled={procesando} sx={btnOutline(FP.color.textSecondary)}>
          Cancelar
        </Button>
      ) : null}
      <Button
        onClick={procesar}
        disabled={procesando || cargando || !puedeUsar || !periodo}
        startIcon={procesando ? <CircularProgress size={16} color="inherit" /> : <PlayArrowOutlinedIcon />}
        sx={btnSolid(FP.color.navy, FP.color.navyDeep)}
      >
        {procesando ? 'Procesando…' : 'Procesar'}
      </Button>
    </>
  );

  if (!puedeUsar) return null;

  if (embedded) {
    return (
      <Box>
        {cuerpo}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>{acciones}</Box>
      </Box>
    );
  }

  return (
    <Dialog
      open={Boolean(open)}
      onClose={cerrar}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ fontWeight: 800 }}>Inicio de nómina</DialogTitle>
      <DialogContent>{cuerpo}</DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>{acciones}</DialogActions>
    </Dialog>
  );
};

export function IniciarNominaPage() {
  const { controller } = useProductosMaterialContext();
  const { roleDetalles, nombreNegocio } = controller;
  const puedeUsar = useMemo(() => puedeUsarIniciarNomina(roleDetalles), [roleDetalles]);

  if (!puedeUsar) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface, p: { xs: 2, md: 3 } }}>
        <PageHeader title="Iniciar nómina" subtitle="Nómina" />
        <Card sx={{ ...cardShellSx, maxWidth: 560, mx: 'auto', mt: 4 }}>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <LockOutlinedIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 1 }} />
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '1.05rem' }}>
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, mt: 0.5, fontSize: '0.92rem' }}>
              No tienes permisos para iniciar la nómina.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Iniciar nómina"
        subtitle={nombreNegocio ? `${nombreNegocio} · Nómina` : 'Nómina'}
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Card sx={cardShellSx}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <IniciarNomina embedded open />
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export default IniciarNomina;
